import { DatePipe, Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { NgbPaginationModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { AccessManager, database } from 'src/app/acces_manager';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { DatepickerComponent } from 'src/app/components/datepicker/datepicker.component';
import { LoadingErrorComponent } from 'src/app/components/loading-error/loading-error.component';
import { ToastComponent } from 'src/app/components/toast/toast.component';
import { ExistObjectPipe } from 'src/app/pipes/filter.pipe';
import { BrowserPipe, CapitalizeWordsPipe } from 'src/app/pipes/format.pipe';
import { BrowserAttestation, WpFilters, WpObjectPromise } from 'src/app/service/global.service';
import { ToastService } from 'src/app/service/toast.service';
import { TranslationService } from 'src/app/service/translation.service';
import { DetailsBrowserAttestationsComponent } from './details-browser-attestations/details-browser-attestations.component';
import { FiltersBAComponent } from 'src/app/components/filters/filters-ba/filters-ba.component';

@Component({
  selector: 'app-list-browser-attestations',
  templateUrl: './list-browser-attestations.component.html',
  styleUrls: ['./list-browser-attestations.component.scss'],
  standalone: true,
  providers: [DatePipe, ToastService, TranslationService],
  imports: [
    TranslateModule,
    NgbPaginationModule,
    NgbTooltipModule,
    LoadingErrorComponent,
    ExistObjectPipe,
    ToastComponent,
    DatepickerComponent,
    CapitalizeWordsPipe,
    BrowserPipe,
    DetailsBrowserAttestationsComponent,
    FiltersBAComponent
  ]

})
export class ListBrowserAttestationsComponent implements OnInit {

  readonly translationService = inject(TranslationService);
  readonly database: AccessManager = database;
  searchmodel = signal('');
  loadingOerror = signal(true);
  listBorn = signal<BrowserAttestation[] | []>([]);
  listAll = signal<BrowserAttestation[] | []>([]);
  promiseResult ?: WpObjectPromise;
  filtersActive = signal<WpFilters>({ pageSize: 50 });
  page = signal(1);
  pageSize = signal(this.filtersActive().pageSize ?? 0);
  selectedBrowserAttestation = signal<BrowserAttestation | undefined>(undefined);

  constructor(
    public datepipe: DatePipe,
    public location: Location,
    public toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.translationService.get('menu-attestations').subscribe(menuAttestations => {
      this.translationService.get('browser-attestations-nav-report-list').subscribe(navReportList => {
        this.translationService.get('list').subscribe(list => {
          document.title = `DIP - ${menuAttestations} - ${navReportList} ${list}`;
        });
      });
    });
    this.getDefaultList();
  }

  today() {
    return Date.now();
  }

  async loading(n: number) {
    await new Promise(r => setTimeout(r, n * 1000));
    this.loadingOerror.set(false);
  }

  awaitChargementListe() {
    const born = this.listBorn();
    return born !== undefined && born.length > 0;
  }

  async getDefaultList(): Promise<void> {
    const filters = this.database.getdata('browserAttestationsFilters');
    if (filters) {
      this.filtersActive.set(filters);
      await this.searchRequestFilters(this.filtersActive());
    } else {
      await this.searchResultWithTimeStamp();
      await this.loading(0.5);
    }
  }

  async searchResultWithTimeStamp() {
    const timeIntervals = [86400000, 604800000];
    const filters = { ...this.filtersActive(), to: Date.now() };
    let found = false;
    for (const interval of timeIntervals) {
      filters.from = filters.to - interval;
      const result = await this.searchAttestations(filters);
      this.promiseResult = result;
      if (result.results) {
        this.listBorn.set(JSON.parse(result.results));
        found = true;
        break;
      }
    }
    if (!found) {
      await this.handleNoResults();
    }
  }

  async searchAttestations(filters: WpFilters) {
    return await Backend.BROWSER_ATTESTATION.search(filters);
  }

  async handleNoResults() {
    this.filtersActive.update(f => ({
      ...f,
      from: undefined,
      to: undefined
    }));
    await this.getAllAttestations();
    this.listBorn.set(this.listAll());
    if (this.listBorn()) {
      this.filtersActive.set({ ...this.filtersActive, from: 1577833200000, to: this.listBorn()[0].createdAt });
    }
  }

  async getAllAttestations(): Promise<void> {
    const result = await this.searchAttestations(this.filtersActive());
    this.promiseResult = result;
    this.listAll.set(JSON.parse(result.results));
  }

  timestampToDate(time: number | undefined) {
    return Backend.FORMAT.timestampToDate(time, this.datepipe, this.translationService.getDatePipeLang()) ?? '';
  }

  titrefiltreDate(debut: number, fin: number) {
    return Backend.FORMAT.formatDate(debut, fin);
  }

  getHoraire(timestamp: number) {
    return Backend.FORMAT.horaire(timestamp, this.datepipe);
  }

  formatId(id: string | undefined) {
    return id ? Backend.FORMAT.formatId(id) : undefined;
  }

  formatAPPName(appName: string) {
    return Backend.FORMAT.formatAPPName(appName);
  }

  majFormID(event: Event, id: string) {
    return event ? this.formatId(id) : id;
  }

  patchCurrentList(promiseObject: WpObjectPromise) {
    if (promiseObject.results) {
      this.listBorn.set(JSON.parse(promiseObject.results));
    } else {
      this.listBorn.set([]);
    }
  }

  async changePage(event: number) {
    this.loadingOerror.set(true);
    if (this.filtersActive()) {
      this.filtersActive.set({ ...this.filtersActive(), page: event });
      const result = await this.searchAttestations(this.filtersActive());
      this.promiseResult = result;
      this.patchCurrentList(result);
      if (this.selectedBrowserAttestation()) {
        this.unsetBrowserAttestation();
      }
    }
    await this.loading(0.15);
  }

  setBrowserAttestation(browserAttestation: BrowserAttestation) {
    this.selectedBrowserAttestation.set(browserAttestation);
  }

  unsetBrowserAttestation() {
    this.selectedBrowserAttestation.set(undefined);
  }

  scrollTo() {
    const element = document.getElementById('detailsBrowser');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async resetAll() {
    this.filtersActive.set({ pageSize: 50 });
    this.loadingOerror.set(true);
    this.searchmodel.set('');
    await this.getDefaultList();
    await this.loading(0.15);
    this.database.setdata('browserAttestationsFilters', this.filtersActive());
  }

  patchMainFilters(filters: WpFilters | undefined) {
    this.filtersActive.set(Backend.WP_FILTRES.patchMainFilters(this.filtersActive(), filters));
  }

async searchByTimestamp([from, to]: [number, number]): Promise<void> {
    const filters = { ...this.filtersActive(), from, to };
    this.filtersActive.set(filters);
    this.patchMainFilters(this.filtersActive());
    this.searchmodel.set('');
    this.loadingOerror.set(true);
    const result = await this.searchAttestations(this.filtersActive());
    this.promiseResult = result;
    this.patchCurrentList(result);
    this.database.setdata('browserAttestationsFilters', this.filtersActive());
    if (this.selectedBrowserAttestation()) {
      this.unsetBrowserAttestation();
    }
    await this.loading(0.15);
  }

  async searchRequestFilters(filters: WpFilters) {
    this.loadingOerror.set(true);
    this.patchMainFilters(filters);
    this.patchSearchModel(filters);
    try {
      const result = await this.searchAttestations(this.filtersActive());
      this.promiseResult = result;
      this.patchCurrentList(result);
      this.database.setdata('browserAttestationsFilters', this.filtersActive());
      await this.loading(0.5);
    } catch (e) {
      const err = e as HttpErrorResponse;
      this.toastService.show(err.error.message, { classname: 'bg-danger', delay: 6000 });
      await this.resetAll();
    }
    this.unsetBrowserAttestation();
  }

  patchSearchModel(event: WpFilters): void {
    this.searchmodel.set('');
    if (event.userId) {
      this.searchmodel.set(event.userId);
    }
    if (event.id) {
      this.searchmodel.set(event.id);
    }
    if (event.metadata) {
      this.searchmodel.set(event.metadata);
    }
  }
}
